/**
 * Generates the JPEG previews Synology's Photo Request upload accepts alongside a file.
 *
 * Why bother: the NAS will happily generate its own, but takes roughly a minute to do it, during which
 * the freshly uploaded item sits in the album with a blank thumbnail. Synology's own web uploader sends
 * previews for exactly this reason, so this mirrors it and the item looks right immediately.
 *
 * Best-effort by design — every failure path returns nothing and the upload proceeds without previews.
 * A codec the browser can't decode (some HEIC, some video containers) is normal, not an error worth
 * showing anyone.
 */

export type Thumbs = { xl?: Blob; sm?: Blob; m?: Blob };

// Widths chosen to match what the NAS serves for each size name, so a supplied preview isn't obviously
// coarser than a generated one at the same slot.
const SIZES: { key: keyof Thumbs; width: number }[] = [
    { key: "xl", width: 1280 },
    { key: "m", width: 480 },
    { key: "sm", width: 240 },
];

const JPEG_QUALITY = 0.85;

/** Video frames need a decode; give up rather than hang a large upload behind a stalled one. */
const VIDEO_FRAME_TIMEOUT_MS = 5000;

const canvasToJpeg = (canvas: HTMLCanvasElement): Promise<Blob | undefined> =>
    new Promise(resolve => canvas.toBlob(blob => resolve(blob ?? undefined), "image/jpeg", JPEG_QUALITY));

async function renderSizes(source: CanvasImageSource, sourceWidth: number, sourceHeight: number): Promise<Thumbs> {
    const thumbs: Thumbs = {};
    if (sourceWidth === 0 || sourceHeight === 0) return thumbs;

    for (const { key, width } of SIZES) {
        // Never upscale: a small original would otherwise be re-encoded larger for no gain.
        const targetWidth = Math.min(width, sourceWidth);
        const targetHeight = Math.max(1, Math.round((sourceHeight / sourceWidth) * targetWidth));

        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const context = canvas.getContext("2d");
        if (!context) continue;
        context.drawImage(source, 0, 0, targetWidth, targetHeight);

        const blob = await canvasToJpeg(canvas);
        if (blob) thumbs[key] = blob;
    }
    return thumbs;
}

async function imageThumbs(file: File): Promise<Thumbs> {
    const url = URL.createObjectURL(file);
    try {
        const image = new Image();
        await new Promise<void>((resolve, reject) => {
            image.onload = () => resolve();
            image.onerror = () => reject(new Error("decode failed"));
            image.src = url;
        });
        return await renderSizes(image, image.naturalWidth, image.naturalHeight);
    } finally {
        URL.revokeObjectURL(url);
    }
}

async function videoThumbs(file: File): Promise<Thumbs> {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    try {
        video.muted = true;
        video.playsInline = true;
        video.preload = "metadata";

        await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error("timed out")), VIDEO_FRAME_TIMEOUT_MS);
            const done = () => { clearTimeout(timeout); resolve(); };
            // seeked, not loadeddata: a frame is only guaranteed to be painted once the seek lands, and
            // seeking slightly into the file avoids the black first frame many recordings start with.
            video.onseeked = done;
            video.onerror = () => { clearTimeout(timeout); reject(new Error("decode failed")); };
            video.onloadedmetadata = () => { video.currentTime = Math.min(0.5, (video.duration || 1) / 2); };
            video.src = url;
        });

        return await renderSizes(video, video.videoWidth, video.videoHeight);
    } finally {
        URL.revokeObjectURL(url);
        video.removeAttribute("src");
    }
}

/** Previews for one file, or an empty object when this browser can't decode it. Never throws. */
export async function generateThumbs(file: File): Promise<Thumbs> {
    try {
        if (file.type.startsWith("image/")) return await imageThumbs(file);
        if (file.type.startsWith("video/")) return await videoThumbs(file);
    } catch {
        // Deliberately silent — see this module's doc.
    }
    return {};
}
